import AppContext from 'App/AppContext';
import { IPDefaultButton, IPLabel, IPPrimaryButton } from 'baseComponents';
import { IPDropdown } from 'baseComponents/Dropdown/IPDropdown';
import { IPPanel, IPPanelType } from 'baseComponents/Panel/IPPanel';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPTextField } from 'baseComponents/TextField/IPTextField';
import { IPSelectableOption } from 'baseComponents/Utils/SelectableOption';
import TextInfo from 'components/Notification/Info';
import AuthSchemaPicker from 'components/Pickers/AuthSchemaPicker';
import {
  globalLayerRightSideBarProps,
  globalStackStylesPanel,
  globalStackStylesPanelFooter,
  globalStackTokensGapLarge,
  globalStackTokensGapSmall,
} from 'globalStyles';
import { TFunction } from 'i18next';
import Joi from 'joi';
import TaskType, { SystemTaskTypes } from 'models/tasks/taskType';
import { FormEvent, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { sortOnString } from 'utils/sorting';

interface IAddTaskTypePanel {
  isOpen: boolean;
  onDismiss: () => void;
  onAddNewTaskType: (item: TaskType) => void;
}

export const AddTaskTypePanel = (props: IAddTaskTypePanel) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['translation', 'forms']);
  const [error, setError] = useState<Joi.ValidationError | undefined>(undefined);
  const [taskTypeToAdd, setTaskTypeToAdd] = useState<TaskType>(new TaskType());
  const [nameError, setNameError] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  //
  // Effects
  //
  useEffect(() => {
    if (props.isOpen) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isOpen]);

  const loadData = async () => {
    try {
      setError(undefined);
      setNameError(undefined);
      setTaskTypeToAdd(getNewTaskType());

      await appContext.globalDataCache.lists.getItems();
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const getNewTaskType = (): TaskType => {
    const newTaskType = new TaskType();

    return newTaskType;
  };

  const _localizedFields: Record<string, string> = {
    name: t('forms:Fields.Name'),
    tabname: t('forms:Fields.Name'),
  };

  const hasChanged = (): boolean => {
    return taskTypeToAdd.name !== '';
  };

  const getFieldError = (fieldName: string): string | undefined => {
    if (fieldName === 'name' && nameError) return nameError;

    return error?.details?.find((e) => {
      return e.context?.key === fieldName;
    })?.message;
  };

  const validate = async (fieldName: string, taskType: TaskType): Promise<string> => {
    const result = taskType.validate(_localizedFields);
    setError(result.error);
    if (result.error) {
      const fieldError = result.error.details.find((e) => {
        return e.context?.key === fieldName;
      });

      if (fieldError) {
        return fieldError.message;
      }
    }

    if (fieldName === 'name') {
      const existingId = await TaskType.nameExists(taskType.name.toLowerCase(), appContext);

      if (existingId && existingId !== taskType.taskTypeId) {
        setNameError(t('forms:Errors.NameError'));
      } else {
        setNameError(undefined);
      }
    }

    return '';
  };

  const disableSave = (): boolean => {
    return !hasChanged() || error || isLoading || nameError ? true : false;
  };

  const onSave = async () => {
    props.onAddNewTaskType(taskTypeToAdd);
  };

  // On Render Footer: Shows Save and Cancel Button at the bottom and performs appropriate actions
  const onRenderFooterContent = () => {
    return (
      <IPStack horizontal wrap styles={globalStackStylesPanelFooter} tokens={globalStackTokensGapSmall}>
        <IPStack.Item>
          <IPPrimaryButton
            disabled={disableSave()}
            onClick={() => {
              onSave();
              props.onDismiss();
            }}
            text={t('translation:General.Button.Save')}
          />
        </IPStack.Item>
        <IPStack.Item>
          <IPDefaultButton onClick={props.onDismiss} text={t('translation:General.Button.Cancel')}></IPDefaultButton>
        </IPStack.Item>
      </IPStack>
    );
  };

  const getSystemTypeOptions = (): IPSelectableOption[] => {
    const options: IPSelectableOption[] = [
      {
        key: SystemTaskTypes.Approval,
        text: TaskType.getSystemTypeText(SystemTaskTypes.Approval, t as TFunction<string[]>),
      },
    ];

    return options.sort((a, b) => sortOnString(a.text, b.text));
  };

  //
  // Main render
  //
  return (
    <IPPanel
      headerText={t('forms:Panels.Add.Title')}
      type={IPPanelType.medium}
      isOpen={props.isOpen}
      isBlocking={true}
      isLightDismiss={true}
      layerProps={globalLayerRightSideBarProps}
      isFooterAtBottom={true}
      onRenderFooterContent={onRenderFooterContent}
      onDismiss={props.onDismiss}
    >
      <IPStack verticalFill styles={globalStackStylesPanel} tokens={globalStackTokensGapLarge}>
        <IPStack.Item>
          <TextInfo message={t('forms:Fields.NameInfo')}>
            <IPLabel>{t('forms:Fields.Name')}</IPLabel>
          </TextInfo>
          <IPTextField
            required
            autoFocus
            value={taskTypeToAdd.name}
            placeholder={t('forms:Fields.NamePlaceholder')}
            onChange={(ev, newValue) => {
              const newTaskType = taskTypeToAdd.clone();
              newTaskType.name = newValue?.trimStart() ?? '';
              validate('name', newTaskType);
              setTaskTypeToAdd(newTaskType);
            }}
            maxLength={512}
            errorMessage={getFieldError('name')}
          />
        </IPStack.Item>
        <IPStack.Item>
          <TextInfo message={t('forms:Fields.TabNameInfo')}>
            <IPLabel>{t('forms:Fields.TabName')}</IPLabel>
          </TextInfo>
          <IPTextField
            value={taskTypeToAdd.tabName}
            placeholder={t('forms:Fields.TabNamePlaceholder')}
            onChange={(ev, newValue) => {
              const newTaskType = taskTypeToAdd.clone();
              newTaskType.tabName = newValue?.trimStart() ?? '';
              validate('tabname', newTaskType);
              setTaskTypeToAdd(newTaskType);
            }}
            maxLength={32}
            errorMessage={getFieldError('tabname')}
          />
        </IPStack.Item>
        <IPStack.Item>
          <TextInfo message={t('forms:Fields.DescriptionInfo')}>
            <IPLabel>{t('forms:Fields.Description')}</IPLabel>
          </TextInfo>
          <IPTextField
            value={taskTypeToAdd?.description ?? ''}
            multiline
            onChange={(ev, newValue) => {
              const newTaskType = taskTypeToAdd.clone();
              newTaskType.description = newValue ?? '';
              setTaskTypeToAdd(newTaskType);
            }}
            placeholder={t('forms:Fields.DescriptionPlaceholder')}
            maxLength={1024}
            style={{
              resize: 'vertical',
              maxHeight: 400,
            }}
          />
        </IPStack.Item>
        <IPStack.Item>
          <TextInfo message={t('forms:Fields.SystemTypeInfo')}>
            <IPLabel>{t('forms:Fields.SystemType')}</IPLabel>
          </TextInfo>
          <IPDropdown
            options={getSystemTypeOptions()}
            styles={{ listbox: { maxHeight: 300 } }}
            selectedKey={taskTypeToAdd.systemTaskType}
            onChange={(event: FormEvent<HTMLDivElement>, option?: IPSelectableOption) => {
              const newTaskType = taskTypeToAdd.clone();
              newTaskType.systemTaskType = option?.key as number;
              setTaskTypeToAdd(newTaskType);
            }}
          />
        </IPStack.Item>
        <IPStack.Item>
          <AuthSchemaPicker
            selectedAuthSchemaId={taskTypeToAdd.authSchemaId}
            onSelect={(schema) => {
              const newTaskType = taskTypeToAdd.clone();
              newTaskType.authSchemaId = schema?.authSchemaId;
              setTaskTypeToAdd(newTaskType);
            }}
          />
        </IPStack.Item>
      </IPStack>
    </IPPanel>
  );
};
